import { AbstractControl, FormGroup, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AppConstants } from '@core-lib/config';
import { FormControlValueTransformer } from '@core-lib/enums';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

export function markFormGroupTouched(formGroup: UntypedFormGroup) {
	(<any>Object).values(formGroup.controls).forEach(control => {
		control.markAsTouched({});
		control.markAsDirty();
		control.updateValueAndValidity({ emitEvent: false });

		if (control.controls) {
			markFormGroupTouched(control);
		}
	});
}

export function markFormGroupUntouched(formGroup: UntypedFormGroup) {
	(<any>Object).values(formGroup.controls).forEach(control => {
		control.markAsUntouched({});
		control.markAsPristine();
		control.updateValueAndValidity({ emitEvent: false });

		if (control.controls) {
			markFormGroupUntouched(control);
		}
	});
}

export function transformControlValue(
	control: AbstractControl,
	rules: FormControlValueTransformer[],
	destroySubscriptions$: Subject<boolean>
): void {
	control.valueChanges
		.pipe(
			filter(value => !!value),
			map(value => {
				let newValue = value;

				if (rules.includes(FormControlValueTransformer.Uppercase)) {
					newValue = newValue.toLocaleUpperCase();
				}

				if (rules.includes(FormControlValueTransformer.RemoveSpecialChars)) {
					newValue = newValue.replace(AppConstants.REGEX.specialChars, '');
				}

				if (rules.includes(FormControlValueTransformer.RemoveNumeric)) {
					newValue = newValue.replace(AppConstants.REGEX.numericChars, '');
				}

				return newValue;
			}),
			takeUntil(destroySubscriptions$)
		)
		.subscribe(value => control.patchValue(value, { emitEvent: false }));
}

export function atLeastOneRequiredBooleanValidator(controls: string[], errorKey: string): ValidatorFn {
	return (group: AbstractControl): ValidationErrors | null => {
		const hasAtLeastOne = controls.some(controlName => group.get(controlName)?.value === true);
		return hasAtLeastOne ? null : { [errorKey]: true };
	};
}

export function uniqueValueValidator(otherFields: string[]): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		if (!control?.parent) return null;

		const currentValue = control.value?.trim()?.toLowerCase();
		if (!currentValue) return null;

		const parent = control.parent as FormGroup;

		const duplicates = otherFields.some(name => {
			const siblingValue = parent.get(name)?.value?.trim()?.toLowerCase();
			return siblingValue && siblingValue === currentValue;
		});

		return duplicates ? { duplicateValue: true } : null;
	};
}
